import { useEffect, useState, useRef } from "react";
import { Link } from "wouter";
import { api, type Lead, type Message, type Note } from "@/lib/api-client";
import { useSession } from "@/lib/auth-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { STATUS_COLORS } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Send,
  Bot,
  BotOff,
  Phone,
  User,
  Calendar,
  Loader2,
  Info,
  StickyNote,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { NotesPanel } from "@/components/NotesPanel";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { useChatRealtime } from "@/lib/useChatRealtime";
import { RenameChatDialog } from "@/components/chat/RenameChatDialog";

interface ChatPageProps {
  id: string;
}
export function ChatPage({ id }: ChatPageProps) {
  const { data: session } = useSession();
  const [lead, setLead] = useState<Lead | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [globalAI, setGlobalAI] = useState<{ enabled: boolean; updatedAt: number | null; updatedBy: string | null } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [isTogglingAI, setIsTogglingAI] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const leadId = parseInt(id);
  const isGlobalAIDisabled = globalAI ? !globalAI.enabled : false;

  const upsertMessage = (incoming: Message) => {
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === incoming.id);
      const next = idx >= 0 ? prev.map((m, i) => (i === idx ? incoming : m)) : [...prev, incoming];
      return [...next].sort((a, b) => a.createdAt - b.createdAt);
    });
  };

  const patchStatus = (input: { messageId: number; deliveryStatus: Message["deliveryStatus"]; deliveredAt?: number; readAt?: number }) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === input.messageId
          ? {
              ...m,
              deliveryStatus: input.deliveryStatus,
              deliveredAt: input.deliveredAt ?? m.deliveredAt,
              readAt: input.readAt ?? m.readAt,
            }
          : m
      )
    );
  };

  const { status: wsStatus } = useChatRealtime({
    leadId,
    onMessage: (payload) => {
      if (payload.leadId !== leadId) return;
      upsertMessage(payload.message as Message);
    },
    onStatusUpdate: (payload) => {
      if (payload.leadId !== leadId) return;
      patchStatus({
        messageId: payload.messageId,
        deliveryStatus: payload.deliveryStatus as Message["deliveryStatus"],
        deliveredAt: payload.deliveredAt,
        readAt: payload.readAt,
      });
    },
    onLeadUpdated: (payload) => {
      const nextLead = payload.lead as Lead;
      if (!nextLead?.id) return;
      if (nextLead.id !== leadId) return;
      setLead((prev) => (prev ? { ...prev, ...nextLead } : nextLead));
    },
  });

  useEffect(() => {
    loadMessages();
    loadNotes();
    void loadGlobalAI();
  }, [id]);

  useEffect(() => {
    // Fallback when WS is down (tab hidden, flaky network, etc.)
    if (wsStatus === "open") return;
    const t = window.setInterval(() => void loadMessages(), 5000);
    return () => window.clearInterval(t);
  }, [wsStatus, id]);

  useEffect(() => {
    // Standard chat UX: newest at bottom; keep scrolled to latest.
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const response = await api.getMessages(leadId, { limit: 100 });
      setLead(response.lead);
      // Chronological so the bottom is the newest message.
      setMessages([...response.messages].sort((a, b) => a.createdAt - b.createdAt));
    } catch (error) {
      console.error("Failed to load messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotes = async () => {
    setIsLoadingNotes(true);
    try {
      const response = await api.getNotes(leadId, { limit: 100 });
      setNotes(response.notes);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const loadGlobalAI = async () => {
    try {
      const res = await api.getGlobalAI();
      setGlobalAI(res);
    } catch (error) {
      console.error("Failed to load global AI:", error);
      setGlobalAI(null);
    }
  };

  // Scroll behavior handled in effect above.

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      await api.sendMessage(leadId, newMessage.trim());
      setNewMessage("");
      if (wsStatus !== "open") await loadMessages();
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleAI = async () => {
    if (!lead) return;
    if (isGlobalAIDisabled) return;
    setIsTogglingAI(true);
    try {
      const response = await api.toggleAIMode(
        leadId,
        !lead.aiEnabled,
        session?.user?.email || undefined
      );
      setLead(response.lead);
      setShowAIDialog(false);
    } catch (error) {
      console.error("Failed to toggle AI:", error);
    } finally {
      setIsTogglingAI(false);
    }
  };

  const handleAddNote = async (content: string) => {
    await api.addNote(leadId, content);
    await loadNotes();
  };

  const handleUpdateNote = async (noteId: number, content: string) => {
    await api.updateNote(leadId, noteId, content);
    await loadNotes();
  };

  const handleDeleteNote = async (noteId: number) => {
    await api.deleteNote(leadId, noteId);
    await loadNotes();
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const openNotes = () => setShowNotes(true);
  const openRename = () => setShowRename(true);

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          <div className="flex justify-start">
            <Skeleton className="h-12 w-2/3 rounded-2xl rounded-bl-md" />
          </div>
          <div className="flex justify-end">
            <Skeleton className="h-20 w-1/2 rounded-2xl rounded-br-md" />
          </div>
          <div className="flex justify-start">
            <Skeleton className="h-16 w-3/4 rounded-2xl rounded-bl-md" />
          </div>
          <div className="flex justify-end">
            <Skeleton className="h-10 w-1/3 rounded-2xl rounded-br-md" />
          </div>
        </div>
      </>
    );
  }

  if (!lead) {
    return (
      <>
        <Header title="Chat Not Found" />
        <div className="p-6 text-center">
          <p className="text-muted-foreground">This chat could not be found.</p>
          <Link href="/chats">
            <Button className="mt-4">Back to Chats</Button>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        title={lead.name || lead.phone}
        subtitle={lead.name ? lead.phone : undefined}
      />

      <div className="flex h-[calc(100dvh-4rem)] overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Back button and AI toggle bar */}
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between bg-card/50">
            <Link href="/chats">
              <Button variant="ghost" size="sm" className="pl-0 sm:pl-3">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="inline">Back</span>
              </Button>
            </Link>

            <div className="flex items-center gap-2 sm:gap-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={openRename}
                title="Rename chat"
              >
                <Pencil className="w-5 h-5 text-muted-foreground" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDetails(true)}
              >
                <Info className="w-5 h-5 text-muted-foreground" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={openNotes}
              >
                <StickyNote className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Notes</span>
              </Button>

              <div className="flex items-center gap-3">
                <Label htmlFor="ai-toggle" className="text-sm text-muted-foreground hidden sm:block">
                  AI Agent
                </Label>
                <Switch
                  id="ai-toggle"
                  checked={lead.aiEnabled}
                  onCheckedChange={() => setShowAIDialog(true)}
                  disabled={isGlobalAIDisabled}
                />
                {lead.aiEnabled ? (
                  <Bot className="w-4 h-4 text-success" />
                ) : (
                  <BotOff className="w-4 h-4 text-warning" />
                )}
              </div>
            </div>
          </div>

          {isGlobalAIDisabled && (
            <div className="px-4 py-2 border-b border-border/60 bg-warning/10 text-warning text-sm flex items-center justify-between gap-3">
              <div>Global AI is OFF (admin). Per-chat AI toggle disabled.</div>
              <Link href="/settings">
                <Button variant="outline" size="sm" className="border-warning/40 text-warning hover:bg-warning/10">
                  Settings
                </Button>
              </Link>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 min-h-0 p-4">
            <div className="max-w-5xl mx-auto space-y-4">
              <ChatMessages
                messages={messages}
                formatDate={formatDate}
                formatTime={formatTime}
                messagesEndRef={messagesEndRef}
              />
            </div>
          </ScrollArea>

          {/* Message Composer */}
          <div className="p-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto relative">
              <div className="flex gap-3 items-end p-2 bg-card border border-border/60 shadow-lg shadow-black/5 rounded-3xl">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  disabled={isSending}
                  className="min-h-[44px] max-h-32 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent py-3 px-4 resize-none shadow-none text-base"
                />
                <Button 
                  type="submit" 
                  disabled={!newMessage.trim() || isSending} 
                  className="h-11 w-11 rounded-full shrink-0 shadow-sm transition-all active:scale-95 mb-0.5 mr-0.5"
                  size="icon"
                >
                  {isSending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 ml-0.5" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* AI Toggle Confirmation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lead.aiEnabled ? "Disable AI Agent?" : "Enable AI Agent?"}
            </DialogTitle>
            <DialogDescription>
              {lead.aiEnabled
                ? "The AI will stop responding to this chat. You will need to reply manually."
                : "The AI will start responding automatically to this chat."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleToggleAI}
              disabled={isTogglingAI}
              variant={lead.aiEnabled ? "destructive" : "default"}
            >
              {isTogglingAI && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {lead.aiEnabled ? "Disable AI" : "Enable AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Details Dialog for Mobile */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <span className="text-sm font-medium text-primary">
                  {(lead.name || lead.phone)?.[0]?.toUpperCase() || "C"}
                </span>
              </div>
              <h2 className="font-semibold text-lg">{lead.name || "Unknown"}</h2>
              <Badge className={`mt-2 capitalize ${STATUS_COLORS[lead.status] || ""}`}>
                {lead.status}
              </Badge>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{lead.phone}</span>
              </div>
              {lead.source && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>Source: {lead.source}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Created on {formatDate(lead.createdAt)}</span>
              </div>
            </div>

            <Card className={lead.aiEnabled ? "border-success/20 bg-success/5" : "border-warning/20 bg-warning/5"}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">AI Agent</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lead.aiEnabled ? "Responding automatically" : "Manual responses only"}
                    </p>
                  </div>
                  {lead.aiEnabled ? (
                    <Bot className="w-6 h-6 text-success" />
                  ) : (
                    <BotOff className="w-6 h-6 text-warning" />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowDetails(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RenameChatDialog
        open={showRename}
        onOpenChange={setShowRename}
        initialName={lead?.name ?? ""}
        onSubmit={async (name) => {
          if (!lead) return;
          const res = await api.renameChat(lead.id, name);
          setLead(res.lead);
        }}
      />

      {/* Notes Dialog for Mobile */}
      <Dialog open={showNotes} onOpenChange={setShowNotes}>
        <DialogContent className="sm:max-w-lg max-h-[80dvh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Notes</DialogTitle>
            <DialogDescription>Add internal notes for this lead.</DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <NotesPanel
              notes={notes}
              onAdd={handleAddNote}
              onUpdate={handleUpdateNote}
              onDelete={handleDeleteNote}
              isLoading={isLoadingNotes}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setShowNotes(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
